// src/App.js
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import SwimDashboardPage from './SwimDashboardPage';
import DashboardPage from './DashboardPage';
import OfficialEventsPage from './OfficialEventsPage';
import AnnouncementsPage from './AnnouncementsPage';
import FriendsDashboard from './FriendsDashboard';
import KnownSwimsPage from './KnownSwimsPage';
import KnownSwimsListPage from './KnownSwimsListPage';
import KnownSwimsDashboardPage from './KnownSwimsDashboardPage';
import CuratedSwimsDashboardPage from './CuratedSwimsDashboardPage';
import { KnownSwimResearchProvider } from './KnownSwimResearchContext';
import { MapboxTokenProvider } from './MapboxTokenContext';
import { RecentSwimsProvider } from './RecentSwimsContext';
import { KnownSwimsProvider } from './KnownSwimsContext';
import './App.css';

// --- Components ---
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError('Failed to log in. Please check your credentials.');
      console.error(err);
    }
  };

  return (
    <div className="app-container"> {/* This centers the login box */}
      <div className="login-container">
        <form onSubmit={handleLogin}>
          <h2>Admin Portal Login</h2>
          {error && <p className="error">{error}</p>}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required />
          <button type="submit">Login</button>
        </form>
      </div>
    </div>
  );
};

const AdminDashboard = ({ mapboxToken }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [knownSwimToEdit, setKnownSwimToEdit] = useState(null);
  const [knownSwimToView, setKnownSwimToView] = useState(null);
  const [knownSwimsMenuOpen, setKnownSwimsMenuOpen] = useState(false);
  const [knownSwimsFocus, setKnownSwimsFocus] = useState(null);

  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <DashboardPage />;
      case 'swims':
        return <SwimDashboardPage onCurateArea={(swim, route) => {
          if (!swim?.geoBoundingBox) return;
          const { minLat, maxLat, minLng, maxLng } = swim.geoBoundingBox;
          setKnownSwimToEdit(null);
          setKnownSwimToView(null);
          setKnownSwimsFocus({
            key: `latest-swim-${swim.id}`,
            coordinates: [
              { latitude: minLat, longitude: minLng },
              { latitude: maxLat, longitude: maxLng }
            ],
            route: route?.geometry?.type === 'LineString' ? route.geometry.coordinates : [],
            pinnedSwim: {
              userID: swim.userID,
              swimID: swim.swimID
            }
          });
          setCurrentView('knownSwims');
        }} />;
      case 'officialEvents':
        return <OfficialEventsPage />;
      case 'announcements':
        return <AnnouncementsPage />;
      case 'friends':
        return <FriendsDashboard />;
      case 'curatedSwimsDashboard':
        return <CuratedSwimsDashboardPage onViewSwim={(swim) => {
          if (!swim) return;
          setKnownSwimToEdit(null);
          setKnownSwimToView(swim);
          setCurrentView('knownSwims');
        }} />;
      case 'knownSwims':
        return <KnownSwimsPage
          initialSwim={knownSwimToEdit}
          onInitialSwimHandled={() => setKnownSwimToEdit(null)}
          initialSelectedSwim={knownSwimToView}
          onInitialSelectedSwimHandled={() => setKnownSwimToView(null)}
          focusRequest={knownSwimsFocus}
          onFocusHandled={() => setKnownSwimsFocus(null)}
        />;
      case 'knownSwimsList':
        return <KnownSwimsListPage onEdit={(swim) => {
          setKnownSwimToEdit(swim);
          setCurrentView('knownSwims');
        }} />;
      case 'knownSwimsDashboard':
        return <KnownSwimsDashboardPage onFocusTimezone={(timezone) => {
          setKnownSwimsFocus(timezone);
          setCurrentView('knownSwims');
        }} />;
      default:
        return <SwimDashboardPage />;
    }
  };

  const viewTitles = {
    dashboard: 'Dashboard',
    swims: 'Latest Swims',
    officialEvents: 'Official Events',
    announcements: 'Announcements',
    friends: 'Friends',
    curatedSwimsDashboard: 'Curated Swims Dashboard',
    knownSwimsDashboard: 'Curated Swims Coverage',
    knownSwims: 'Curated Swims Map',
    knownSwimsList: 'Curated Swims List'
  };

  const headerTitle = viewTitles[currentView] || 'Latest Swims';

  return (
    <MapboxTokenProvider value={mapboxToken}>
      <KnownSwimResearchProvider>
        <RecentSwimsProvider>
        <KnownSwimsProvider>
        <div
          className={`overlay ${isSidebarOpen ? 'open' : ''}`}
          onClick={() => setIsSidebarOpen(false)}
        ></div>
        <div className="dashboard-container">
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div>
            <h2>OpenWater Admin</h2>
            <nav className="sidebar-nav">
              <ul>
                <li>
                  <a 
                    href="#dashboard" 
                    className={currentView === 'dashboard' ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); setCurrentView('dashboard'); setIsSidebarOpen(false); }}
                  >
                    <span>Dashboard</span>
                  </a>
                </li>
                <li className="known-swims-nav-group">
                  <a
                    href="#curated-swims"
                    className={currentView === 'curatedSwimsDashboard' || currentView.startsWith('knownSwims') ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      setKnownSwimsMenuOpen(true);
                      setKnownSwimToEdit(null);
                      setKnownSwimToView(null);
                      setCurrentView('curatedSwimsDashboard');
                      setIsSidebarOpen(false);
                    }}
                  >
                    <span>Curated Swims</span>
                  </a>
                  {knownSwimsMenuOpen && (
                    <ul className="known-swims-subnav">
                      <li><a href="#curated-swims-dashboard" className={currentView === 'curatedSwimsDashboard' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setCurrentView('curatedSwimsDashboard'); setIsSidebarOpen(false); }}>Dashboard</a></li>
                      <li><a href="#curated-swims-coverage" className={currentView === 'knownSwimsDashboard' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setCurrentView('knownSwimsDashboard'); setIsSidebarOpen(false); }}>Coverage</a></li>
                      <li><a href="#curated-swims-map" className={currentView === 'knownSwims' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setKnownSwimToEdit(null); setKnownSwimToView(null); setCurrentView('knownSwims'); setIsSidebarOpen(false); }}>Map</a></li>
                      <li><a href="#curated-swims-list" className={currentView === 'knownSwimsList' ? 'active' : ''} onClick={(e) => { e.preventDefault(); setCurrentView('knownSwimsList'); setIsSidebarOpen(false); }}>List</a></li>
                    </ul>
                  )}
                </li>
                <li>
                  <a 
                    href="#swims" 
                    className={currentView === 'swims' ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); setCurrentView('swims'); setIsSidebarOpen(false); }}
                  >
                    <span>Latest Swims</span>
                  </a>
                </li>
                <li>
                  <a 
                    href="#official-events" 
                    className={currentView === 'officialEvents' ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); setCurrentView('officialEvents'); setIsSidebarOpen(false); }}
                  >
                    <span>Official Events</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#announcements"
                    className={currentView === 'announcements' ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); setCurrentView('announcements'); setIsSidebarOpen(false); }}
                  >
                    <span>Announcements</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#friends"
                    className={currentView === 'friends' ? 'active' : ''}
                    onClick={(e) => { e.preventDefault(); setCurrentView('friends'); setIsSidebarOpen(false); }}
                  >
                    <span>Friends</span>
                  </a>
                </li>
              </ul>
            </nav>
          </div>
          <button onClick={() => signOut(auth)}>Logout</button>
        </aside>

        <div className="content-wrapper">
          <header className="mobile-header">
            <button className="menu-toggle" onClick={() => setIsSidebarOpen(true)}>
              <img src="/menu-icon.svg" alt="Menu"/>
            </button>
            <div className="mobile-header-title">
              {headerTitle}
            </div>
          </header>

          <main className="content-area">
            {renderView()}
          </main>
        </div>
        </div>
        </KnownSwimsProvider>
        </RecentSwimsProvider>
      </KnownSwimResearchProvider>
    </MapboxTokenProvider>
  );
};

// --- Main App Logic
function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [mapboxToken, setMapboxToken] = useState(null);
  const [configurationError, setConfigurationError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setIsLoading(true);
      setConfigurationError(null);
      if (currentUser) {
        const idTokenResult = await currentUser.getIdTokenResult(true);
        if (idTokenResult.claims.admin) {
          try {
            const configuration = await getDoc(doc(db, 'AdminConfiguration', 'mapbox'));
            const token = configuration.data()?.token;
            if (!configuration.exists() || typeof token !== 'string' || token.length === 0) {
              throw new Error('Mapbox token configuration is missing.');
            }
            setMapboxToken(token);
            setUser(currentUser);
            setIsAdmin(true);
          } catch (error) {
            console.error('Unable to load admin map configuration:', error);
            setUser(null);
            setIsAdmin(false);
            setMapboxToken(null);
            setConfigurationError('Admin map configuration is unavailable.');
          }
        } else {
          alert("Access Denied");
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          setMapboxToken(null);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setMapboxToken(null);
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (isLoading) {
    return <div className="app-container">Loading...</div>;
  }

  if (configurationError) {
    return <div className="app-container error">{configurationError}</div>;
  }

  return isAdmin && user && mapboxToken ? <AdminDashboard mapboxToken={mapboxToken} /> : <LoginPage />;
}

export default App;
